/**
 * Simplified Vue Mixin for Dropdown Accessibility
 *
 * This mixin provides keyboard navigation for dropdowns.
 * Pass trigger and option selectors as props or use defaults.
 */

import Vue from 'vue';

export const DropdownAccessibilityMixin = {
    props: {
        triggerSelectors: {
            type: Array,
            default: () => [
                '[role="combobox"]',
                '.new-input__container--dropdown',
                '.new-input-black__container--dropdown', // Add black variant
                '.dropdown__current',
                '#container-country',
                '#container-subject',
                '#container-brandStore',
                '#searchBrandstore', // Add the specific container ID
            ],
        },
        optionSelectors: {
            type: Array,
            default: () => [
                '.dropdown__options-option.new-input__dropdown__options__option', // Standard variant
                '.dropdown__options-option.new-input__dropdown__options__option--black', // Black variant
                '.dropdown__options-option', // Fallback for basic dropdown options
                '[role="option"][class*="dropdown__options-option"]', // Role-based with class validation
                '.new-input__dropdown__options__option--black', // Direct black variant selector
            ],
        },
    },

    data() {
        return {
            dropdownTriggers: [],
            dropdownOptions: [],
            mutationObserver: null, // Add MutationObserver instance
            activeOptionId: null, // Track currently active option for ARIA
        };
    },

    mounted() {
        this.$nextTick(() => {
            this.setupDropdownAccessibility();
            this.setupMutationObserver(); // Setup class change monitoring
            this.initializeDropdownStates(); // Initialize proper states for existing dropdowns
        });
    },

    updated() {
        // Re-setup accessibility when DOM updates (e.g., new dropdowns appear)
        this.$nextTick(() => {
            this.smartRefreshDropdownAccessibility();
        });
    },

    beforeDestroy() {
        this.cleanupDropdownAccessibility();
        this.cleanupMutationObserver(); // Clean up MutationObserver
    },

    methods: {
        setupDropdownAccessibility() {
            this.setupTriggers();
            this.setupOptions();
        },

        refreshDropdownAccessibility() {
            // Clean up existing listeners first
            this.cleanupDropdownAccessibility();
            this.cleanupMutationObserver();
            // Then setup again with any new elements
            this.setupDropdownAccessibility();
            this.setupMutationObserver();
        },

        smartRefreshDropdownAccessibility() {
            // Only clean up handlers for elements that no longer exist in the DOM
            this.dropdownTriggers = this.dropdownTriggers.filter(
                ({ element, handler }) => {
                    if (!this.$el.contains(element)) {
                        element.removeEventListener('keydown', handler);
                        return false; // Remove from array
                    }
                    return true; // Keep in array
                },
            );

            this.dropdownOptions = this.dropdownOptions.filter(
                ({ element, keydownHandler, focusHandler }) => {
                    if (!this.$el.contains(element)) {
                        element.removeEventListener('keydown', keydownHandler);
                        element.removeEventListener('focus', focusHandler);
                        return false; // Remove from array
                    }
                    return true; // Keep in array
                },
            );

            // Then setup new elements (duplicate detection in setupTriggers/setupOptions will prevent re-adding existing ones)
            this.setupDropdownAccessibility();

            // Refresh MutationObserver to include any new triggers
            this.refreshMutationObserver();
        },

        // Manual method for components to call when they add new dropdowns
        setupNewDropdowns() {
            this.$nextTick(() => {
                this.refreshDropdownAccessibility();
            });
        },

        // Method specifically for multi-step wizards - call this when a new step loads
        setupDropdownsForNewStep() {
            this.$nextTick(() => {
                // Small delay to ensure DOM is fully rendered
                setTimeout(() => {
                    this.refreshDropdownAccessibility();
                }, 100);
            });
        },

        cleanupDropdownAccessibility() {
            // Clean up trigger event listeners
            this.dropdownTriggers.forEach(({ element, handler }) => {
                element.removeEventListener('keydown', handler);
            });
            this.dropdownTriggers = [];

            // Clean up option event listeners
            this.dropdownOptions.forEach(
                ({ element, keydownHandler, focusHandler }) => {
                    element.removeEventListener('keydown', keydownHandler);
                    element.removeEventListener('focus', focusHandler);
                },
            );
            this.dropdownOptions = [];
        },

        setupTriggers() {
            let triggers = [];

            // Find triggers using provided selectors
            this.triggerSelectors.forEach((selector) => {
                const foundTriggers = this.$el.querySelectorAll(selector);
                foundTriggers.forEach((trigger) => {
                    if (!triggers.includes(trigger)) {
                        triggers.push(trigger);
                    }
                });
            });

            triggers.forEach((trigger) => {
                // Skip if already has a handler to avoid duplicates
                const existingHandler = this.dropdownTriggers.find(
                    (item) => item.element === trigger,
                );
                if (existingHandler) {
                    return;
                }

                const keydownHandler = (event) => {
                    this.handleTriggerKeydown(event, trigger);
                };

                trigger.addEventListener('keydown', keydownHandler);

                // Ensure trigger is focusable
                if (!trigger.hasAttribute('tabindex')) {
                    trigger.setAttribute('tabindex', '0');
                }

                this.dropdownTriggers.push({
                    element: trigger,
                    handler: keydownHandler,
                });
            });
        },

        setupOptions() {
            let options = [];

            // Find options using provided selectors, but exclude containers and triggers
            this.optionSelectors.forEach((selector) => {
                const foundOptions = this.$el.querySelectorAll(selector);

                foundOptions.forEach((option) => {
                    // Skip containers and trigger elements - only include actual option elements
                    if (
                        !option.classList.contains(
                            'new-input__dropdown__options-container',
                        ) &&
                        !option.classList.contains(
                            'new-input__dropdown__options-container--black',
                        ) && // Add black variant container exclusion
                        !option.classList.contains('dropdown__options') &&
                        !option.classList.contains(
                            'dropdown__options-holder',
                        ) && // Add this exclusion
                        !option.classList.contains(
                            'new-input__dropdown__options-holder',
                        ) && // Add this exclusion
                        !option.classList.contains(
                            'new-input__dropdown__options-pays-holder-black',
                        ) && // Add black pays holder exclusion
                        !option.classList.contains(
                            'new-input__container--dropdown',
                        ) && // Exclude trigger elements
                        !option.classList.contains(
                            'new-input-black__container--dropdown',
                        ) && // Exclude black trigger elements
                        option.getAttribute('role') !== 'combobox' && // Exclude combobox triggers
                        option.getAttribute('role') !== 'listbox' && // Exclude listbox containers
                        !options.includes(option)
                    ) {
                        options.push(option);
                    }
                });
            });

            // Group options by their parent dropdown for proper navigation
            const optionsByDropdown = new Map();
            options.forEach((option) => {
                const dropdown = option.closest('.dropdown');
                if (dropdown) {
                    if (!optionsByDropdown.has(dropdown)) {
                        optionsByDropdown.set(dropdown, []);
                    }
                    optionsByDropdown.get(dropdown).push(option);
                }
            });

            // Setup handlers for each group of options
            optionsByDropdown.forEach((dropdownOptions, dropdown) => {
                dropdownOptions.forEach((option, index) => {
                    const keydownHandler = (event) => {
                        this.handleOptionKeydownInternal(
                            event,
                            option,
                            index,
                            dropdownOptions,
                        );
                    };

                    const focusHandler = (event) => {
                        this.handleOptionFocus(event, option, dropdownOptions);
                    };

                    option.addEventListener('keydown', keydownHandler);
                    option.addEventListener('focus', focusHandler);

                    // Set basic accessibility attributes
                    if (!option.hasAttribute('role')) {
                        option.setAttribute('role', 'option');
                    }
                    if (!option.hasAttribute('aria-selected')) {
                        option.setAttribute('aria-selected', 'false');
                    }

                    // All options start with tabindex="-1" when dropdown is closed
                    option.setAttribute('tabindex', '-1');

                    this.dropdownOptions.push({
                        element: option,
                        keydownHandler,
                        focusHandler,
                    });
                });

                // Setup listbox accessibility for each dropdown
                this.setupListboxAccessibility(dropdown, dropdownOptions);
            });
        },

        setupListboxAccessibility(dropdown, options) {
            // Find the listbox container within this dropdown
            const listbox = dropdown.querySelector('[role="listbox"]');
            if (listbox) {
                // Make listbox non-focusable when closed
                listbox.setAttribute('tabindex', '-1');

                // Add aria-expanded state management
                const isActive =
                    dropdown.classList.contains('dropdown--active') ||
                    dropdown.classList.contains('dropdown-active');
                listbox.setAttribute('aria-expanded', isActive.toString());

                // Handle aria-hidden with WCAG 2.2 AA compliance
                if (!isActive) {
                    // WCAG 2.2 AA compliance: Ensure no child elements are focused before hiding
                    const focusedElement = document.activeElement;
                    const hasFocusedChild = listbox.contains(focusedElement);

                    if (hasFocusedChild) {
                        // Move focus away from the dropdown options to prevent aria-hidden violation
                        focusedElement.blur();
                    }

                    listbox.setAttribute('aria-hidden', 'true');
                } else {
                    listbox.removeAttribute('aria-hidden');
                }
            }
        },

        initializeDropdownStates() {
            // Initialize all dropdown states on component mount
            const dropdowns = this.$el.querySelectorAll('.dropdown');
            dropdowns.forEach((dropdown) => {
                this.handleDropdownClassChange(dropdown);
            });
        },

        handleTriggerKeydown(event, trigger) {
            if (event.key === ' ' || event.key === 'Enter') {
                // For input elements, handle spacebar and Enter differently
                if (trigger.tagName.toLowerCase() === 'input') {
                    if (event.key === 'Enter') {
                        event.preventDefault();
                        trigger.focus();
                        // Trigger any associated dropdown opening logic
                        const inputEvent = new Event('input', {
                            bubbles: true,
                        });
                        trigger.dispatchEvent(inputEvent);
                    } else if (event.key === ' ') {
                        // For spacebar on input, don't prevent default (allow typing)
                        // But still trigger the input event to ensure dropdown logic runs
                        const inputEvent = new Event('input', {
                            bubbles: true,
                        });
                        trigger.dispatchEvent(inputEvent);

                        // After a short delay, refresh dropdown accessibility to ensure
                        // new options are properly set up for keyboard navigation
                        setTimeout(() => {
                            this.smartRefreshDropdownAccessibility();
                        }, 100);

                        return; // Let spacebar pass through naturally
                    }
                } else {
                    // For non-input elements, prevent default for both spacebar and Enter
                    event.preventDefault();
                    trigger.click();
                }

                // Run the dropdown focus logic for non-input elements or Enter on inputs
                if (
                    trigger.tagName.toLowerCase() !== 'input' ||
                    event.key === 'Enter'
                ) {
                    setTimeout(() => {
                        // Find the dropdown and update tabindex for options
                        const dropdown = trigger.closest('.dropdown');

                        if (dropdown) {
                            // Look for actual option elements within this specific dropdown
                            const firstOption = dropdown.querySelector(
                                '.dropdown__options-option.new-input__dropdown__options__option, .dropdown__options-option.new-input__dropdown__options__option--black, .dropdown__options-option, [role="option"]:not(.new-input__dropdown__options-container):not(.new-input__dropdown__options-container--black)',
                            );

                            if (
                                firstOption &&
                                (dropdown.classList.contains(
                                    'dropdown--active',
                                ) ||
                                    dropdown.classList.contains(
                                        'dropdown-active',
                                    ))
                            ) {
                                firstOption.focus();
                            }
                        }
                    }, 50);
                }
            }
        },

        handleOptionKeydown(event, option, index, allOptions) {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    const nextIndex =
                        index < allOptions.length - 1 ? index + 1 : 0;
                    allOptions[nextIndex].focus();
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    const prevIndex =
                        index > 0 ? index - 1 : allOptions.length - 1;
                    allOptions[prevIndex].focus();
                    break;

                case 'Enter':
                case ' ':
                    event.preventDefault();
                    event.stopPropagation();
                    option.click();
                    Vue.nextTick(() => {
                        this.closeDropdown(option);
                    });
                    break;

                case 'Escape':
                    event.preventDefault();
                    this.closeDropdown(option);
                    break;

                case 'Home':
                    event.preventDefault();
                    allOptions[0].focus();
                    break;

                case 'End':
                    event.preventDefault();
                    allOptions[allOptions.length - 1].focus();
                    break;
            }
        },

        handleOptionFocus(event, option, dropdownOptions) {
            // Update aria-selected states only within this dropdown
            dropdownOptions.forEach((opt) =>
                opt.setAttribute('aria-selected', 'false'),
            );
            option.setAttribute('aria-selected', 'true');
        },

        closeDropdown(option) {
            const dropdown = option.closest('.dropdown');
            if (dropdown) {
                dropdown.classList.remove('dropdown--active');
                dropdown.classList.remove('dropdown-active'); // Also remove single-dash variant

                const trigger = dropdown.querySelector(
                    '[role="combobox"], .new-input__container--dropdown, .new-input-black__container--dropdown, .new-input__container--input, input[type="text"]',
                );
                if (trigger) {
                    trigger.focus();
                }
            }
        },

        setupMutationObserver() {
            // Create MutationObserver to watch for class changes on dropdown containers
            this.mutationObserver = new MutationObserver((mutations) => {
                let shouldRefresh = false;

                mutations.forEach((mutation) => {
                    if (
                        (mutation.type === 'attributes' &&
                            mutation.attributeName === 'class') ||
                        mutation.type === 'childList'
                    ) {
                        const target = mutation.target;

                        // Check for new nodes that might contain dropdowns
                        if (mutation.addedNodes.length > 0) {
                            mutation.addedNodes.forEach((node) => {
                                // Check if the added node or its children contain dropdown elements
                                if (node.nodeType === Node.ELEMENT_NODE) {
                                    const hasDropdowns =
                                        node.classList?.contains('dropdown') ||
                                        (node.querySelector &&
                                            (node.querySelector('.dropdown') ||
                                                node.querySelector(
                                                    '[role="combobox"]',
                                                ) ||
                                                node.querySelector(
                                                    '.new-input__container--dropdown',
                                                ) ||
                                                node.querySelector(
                                                    '.new-input-black__container--dropdown',
                                                )));

                                    if (hasDropdowns) {
                                        shouldRefresh = true;
                                    }
                                }
                            });
                        }

                        // Check if the target is a dropdown container with dropdown--active class change
                        if (
                            target.classList &&
                            target.classList.contains('dropdown')
                        ) {
                            this.handleDropdownClassChange(target);
                        }
                    }
                });

                // Refresh accessibility setup if new dropdowns were detected
                if (shouldRefresh) {
                    this.smartRefreshDropdownAccessibility();
                }
            });

            // Observe the entire component element for changes
            this.mutationObserver.observe(this.$el, {
                attributes: true,
                childList: true,
                subtree: true,
                attributeFilter: ['class'],
            });

            // Also observe specific dropdown containers if they exist
            const dropdownContainers = this.$el.querySelectorAll('.dropdown');
            dropdownContainers.forEach((dropdown) => {
                this.mutationObserver.observe(dropdown, {
                    attributes: true,
                    childList: true,
                    subtree: true,
                    attributeFilter: ['class'],
                });
            });
        },

        cleanupMutationObserver() {
            if (this.mutationObserver) {
                this.mutationObserver.disconnect();
                this.mutationObserver = null;
            }
        },

        handleDropdownClassChange(dropdownContainer) {
            // Find all option elements within this dropdown container
            const nestedOptions = dropdownContainer.querySelectorAll(
                '.dropdown__options-option.new-input__dropdown__options__option, .dropdown__options-option.new-input__dropdown__options__option--black, .dropdown__options-option, [role="option"]',
            );

            // Find the listbox within this dropdown
            const listbox = dropdownContainer.querySelector('[role="listbox"]');

            // Check if dropdown container has dropdown--active OR dropdown-active class
            const isActive =
                dropdownContainer.classList.contains('dropdown--active') ||
                dropdownContainer.classList.contains('dropdown-active');

            Vue.nextTick(() => {
                // Manage option focus states
                nestedOptions.forEach((option, index) => {
                    if (isActive) {
                        // When dropdown is active, first option gets tabindex="0", others get "-1"
                        option.setAttribute(
                            'tabindex',
                            index === 0 ? '0' : '-1',
                        );
                    } else {
                        // When dropdown is closed, all options should be non-focusable
                        option.setAttribute('tabindex', '-1');

                        // WCAG 2.2 AA compliance: Remove focus from option elements when dropdown closes
                        // to prevent aria-hidden being applied to focused elements
                        if (document.activeElement === option) {
                            option.blur();
                        }
                    }
                });

                // Manage listbox accessibility
                if (listbox) {
                    listbox.setAttribute('aria-expanded', isActive.toString());

                    if (isActive) {
                        // Show to screen readers when open
                        listbox.removeAttribute('aria-hidden');
                        listbox.setAttribute('tabindex', '-1'); // Listbox itself should not be focusable
                    } else {
                        // WCAG 2.2 AA compliance: Ensure no child elements are focused before hiding
                        // Check if any element within the listbox currently has focus
                        const focusedElement = document.activeElement;
                        const hasFocusedChild =
                            listbox.contains(focusedElement);

                        if (hasFocusedChild) {
                            // Move focus away from the dropdown options to prevent aria-hidden violation
                            focusedElement.blur();

                            // Find the trigger element and return focus to it
                            const trigger = dropdownContainer.querySelector(
                                '[role="combobox"], .new-input__container--dropdown, .new-input-black__container--dropdown, .dropdown__current',
                            );
                            if (trigger && trigger.focus) {
                                trigger.focus();
                            }
                        }

                        // Now it's safe to hide from screen readers when closed
                        listbox.setAttribute('aria-hidden', 'true');
                        listbox.setAttribute('tabindex', '-1');
                    }
                }
            });
        },

        refreshMutationObserver() {
            // Clean up existing observer
            this.cleanupMutationObserver();

            // Setup new observer with current triggers
            if (this.dropdownTriggers.length > 0) {
                this.setupMutationObserver();
            }
        },

        // Wrapper method for template compatibility
        handleOptionKeydown(event, store) {
            // This method is called directly from templates
            // We need to find the option element and its context
            const option = event.target;
            const dropdown = option.closest('.dropdown');

            if (!dropdown) return;

            // Find all options in this dropdown
            const allOptions = Array.from(
                dropdown.querySelectorAll(
                    '.dropdown__options-option.new-input__dropdown__options__option, .dropdown__options-option.new-input__dropdown__options__option--black, .dropdown__options-option, [role="option"]',
                ),
            );

            const index = allOptions.indexOf(option);

            if (index !== -1) {
                // Call the original method with proper parameters
                this.handleOptionKeydownInternal(
                    event,
                    option,
                    index,
                    allOptions,
                );
            }
        },

        // Renamed the original method to avoid conflicts
        handleOptionKeydownInternal(event, option, index, allOptions) {
            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    const nextIndex =
                        index < allOptions.length - 1 ? index + 1 : 0;
                    allOptions[nextIndex].focus();
                    break;

                case 'ArrowUp':
                    event.preventDefault();
                    const prevIndex =
                        index > 0 ? index - 1 : allOptions.length - 1;
                    allOptions[prevIndex].focus();
                    break;

                case 'Enter':
                case ' ':
                    event.preventDefault();
                    event.stopPropagation();
                    option.click();
                    Vue.nextTick(() => {
                        this.closeDropdown(option);
                    });
                    break;

                case 'Escape':
                    event.preventDefault();
                    this.closeDropdown(option);
                    break;

                case 'Home':
                    event.preventDefault();
                    allOptions[0].focus();
                    break;

                case 'End':
                    event.preventDefault();
                    allOptions[allOptions.length - 1].focus();
                    break;
            }
        },

        // Method for setting active option (called from templates)
        setActiveOption(optionId) {
            // Update the activeOptionId for ARIA
            this.activeOptionId = 'store-option-' + optionId;

            // Find the option element and update focus/selection states
            const option = document.getElementById(this.activeOptionId);
            if (option) {
                // Update aria-selected states within the dropdown
                const dropdown = option.closest('.dropdown');
                if (dropdown) {
                    const allOptions =
                        dropdown.querySelectorAll('[role="option"]');
                    allOptions.forEach((opt) =>
                        opt.setAttribute('aria-selected', 'false'),
                    );
                    option.setAttribute('aria-selected', 'true');
                }
            }
        },
    },
};
